import { Building2, ChevronRight, MoreVertical, Package, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ACTIVITY_STATUS_LABEL,
  type ActivityStatus,
} from '@obraplan/core/domain/structure/activity';
import type { StructureTree as StructureTreeData } from '@obraplan/core/domain/structure/structure-tree';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import { flattenTree, type FlatTreeNode } from './tree-model';

const STATUS_TONE: Readonly<Record<ActivityStatus, 'neutral' | 'info' | 'ok' | 'warn'>> = {
  PLANNED: 'neutral',
  IN_PROGRESS: 'info',
  DONE: 'ok',
  CANCELLED: 'warn',
};

export interface StructureTreeProps {
  tree: StructureTreeData;
  expanded: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onActivate?: (node: FlatTreeNode) => void;
  /** Renomear torre ou pavimento. Ausente = perfil sem `structure.manage`. */
  onRename?: (node: FlatTreeNode) => void;
  /** Remover o nó. Ausente = perfil sem permissão para o nível em questão. */
  onDelete?: (node: FlatTreeNode) => void;
  /** Lançar item na atividade. Ausente = perfil sem `schedule.edit`. */
  onAddItem?: (node: FlatTreeNode) => void;
  /** Editar o item. Ausente = perfil sem `schedule.edit`. */
  onEditItem?: (node: FlatTreeNode) => void;
}

/**
 * O que cada nível aceita.
 *
 * A atividade não é renomeável: sua identidade é (pavimento, disciplina) — RN-008. O
 * item também não: a dele é (atividade, material) — RN-024. Editar item existe, mas é
 * outra ação: muda quantidade, unidade e datas, nunca de que material se trata.
 */
function canRename(node: FlatTreeNode): boolean {
  return node.type === 'TOWER' || node.type === 'FLOOR';
}

/**
 * Árvore da obra.
 *
 * ```text
 * OBRA → TORRE → PAVIMENTO → DISCIPLINA(ATIVIDADE) → ITEM
 * ```
 *
 * O pavimento aparece **uma única vez**, com as disciplinas abaixo — a normalização
 * exigida pelo requisito, visível na tela.
 *
 * Desenhada como o corte do prédio: cada pavimento leva o número do nível num marcador
 * à esquerda, como a cota de um corte; cada disciplina, um traço na cor dela, a mesma
 * do cronograma; e linhas-guia descem de cada nó aberto até o último filho, para que a
 * profundidade se leia sem contar recuos.
 *
 * Segue o padrão ARIA de árvore: `tabindex` móvel (só um nó é focável por vez), setas
 * para navegar, `aria-expanded` e `aria-level`. Nó recolhido não renderiza os filhos, o
 * que mantém o DOM pequeno mesmo em obra grande.
 *
 * Teclas: setas navegam, `Home`/`End` vão aos extremos, `Enter` abre e fecha, `F2`
 * renomeia e `Delete` remove — as duas últimas são o equivalente de teclado do menu de
 * ações, que fica fora da ordem de tabulação para não quebrar o `tabindex` móvel.
 */
export function StructureTree({
  tree,
  expanded,
  onToggle,
  onActivate,
  onRename,
  onDelete,
  onAddItem,
  onEditItem,
}: StructureTreeProps) {
  const nodes = useMemo(() => flattenTree(tree, expanded), [tree, expanded]);
  const [focusedId, setFocusedId] = useState<string | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeId =
    focusedId !== undefined && nodes.some((n) => n.id === focusedId) ? focusedId : nodes[0]?.id;

  const focusNode = useCallback((id: string) => {
    setFocusedId(id);
    const element = containerRef.current?.querySelector<HTMLElement>(`[data-node-id="${id}"]`);
    element?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, node: FlatTreeNode, index: number) => {
      const isExpanded = expanded.has(node.id);

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const next = nodes[index + 1];
          if (next !== undefined) focusNode(next.id);
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const previous = nodes[index - 1];
          if (previous !== undefined) focusNode(previous.id);
          break;
        }
        case 'ArrowRight': {
          event.preventDefault();
          if (node.hasChildren && !isExpanded) {
            onToggle(node.id);
          } else if (node.hasChildren) {
            const child = nodes[index + 1];
            if (child !== undefined && child.parentId === node.id) focusNode(child.id);
          }
          break;
        }
        case 'ArrowLeft': {
          event.preventDefault();
          if (node.hasChildren && isExpanded) {
            onToggle(node.id);
          } else if (node.parentId !== undefined) {
            focusNode(node.parentId);
          }
          break;
        }
        case 'Home': {
          event.preventDefault();
          const first = nodes[0];
          if (first !== undefined) focusNode(first.id);
          break;
        }
        case 'End': {
          event.preventDefault();
          const last = nodes[nodes.length - 1];
          if (last !== undefined) focusNode(last.id);
          break;
        }
        case 'Enter':
        case ' ': {
          event.preventDefault();
          if (node.hasChildren) onToggle(node.id);
          else onActivate?.(node);
          break;
        }
        // F2 e Delete: as ações do nó sem passar pelo menu. O botão do menu fica fora da
        // ordem de tabulação para não quebrar o `tabindex` móvel da árvore, então quem
        // navega pelo teclado precisa destes atalhos.
        case 'F2': {
          event.preventDefault();
          if (node.type === 'ITEM') onEditItem?.(node);
          else if (onRename !== undefined && canRename(node)) onRename(node);
          break;
        }
        case 'Delete': {
          if (onDelete === undefined) break;
          event.preventDefault();
          onDelete(node);
          break;
        }
        default:
          break;
      }
    },
    [expanded, focusNode, nodes, onActivate, onDelete, onEditItem, onRename, onToggle],
  );

  const actionsFor = useCallback(
    (node: FlatTreeNode) => ({
      rename: onRename !== undefined && canRename(node),
      addItem: onAddItem !== undefined && node.type === 'ACTIVITY',
      editItem: onEditItem !== undefined && node.type === 'ITEM',
      remove: onDelete !== undefined,
    }),
    [onAddItem, onDelete, onEditItem, onRename],
  );

  return (
    <div
      ref={containerRef}
      role="tree"
      aria-label="Estrutura da obra"
      // O recuo de cada nível é uma variável: menor no celular, onde cada rem conta, e
      // partilhada pelas linhas-guia, que precisam cair exatamente sob o chevron do pai.
      className="flex flex-col [--recuo:1rem] md:[--recuo:1.5rem]"
    >
      {nodes.map((node, index) => {
        const isExpanded = expanded.has(node.id);
        const depth = node.level - 1;

        return (
          <div
            key={node.id}
            data-node-id={node.id}
            data-node-type={node.type}
            role="treeitem"
            aria-level={node.level}
            aria-posinset={node.positionInSet}
            aria-setsize={node.setSize}
            {...(node.hasChildren ? { 'aria-expanded': isExpanded } : {})}
            tabIndex={node.id === activeId ? 0 : -1}
            onKeyDown={(event) => handleKeyDown(event, node, index)}
            onFocus={() => setFocusedId(node.id)}
            onClick={() => {
              if (node.hasChildren) onToggle(node.id);
              else onActivate?.(node);
            }}
            className={cn(
              'group relative flex cursor-pointer items-center gap-2 rounded-md pr-2 text-sm transition-colors',
              'hover:bg-(--color-surface-muted)',
              node.type === 'TOWER' && 'mt-3 py-2 text-base font-semibold first:mt-0',
              node.type === 'FLOOR' && 'py-1.5 font-medium',
              node.type === 'ACTIVITY' && 'py-1.5',
              node.type === 'ITEM' && 'py-1 text-(--color-content-muted)',
            )}
            style={{ paddingLeft: `calc(0.5rem + ${String(depth)} * var(--recuo))` }}
          >
            {/* Linhas-guia: uma por ancestral, sob o chevron dele. */}
            {Array.from({ length: depth }, (_, ancestor) => (
              <span
                key={ancestor}
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 w-px bg-(--color-border-subtle)"
                style={{ left: `calc(1rem + ${String(ancestor)} * var(--recuo))` }}
              />
            ))}

            <ChevronRight
              className={cn(
                'size-4 shrink-0 text-(--color-content-muted) transition-transform',
                isExpanded && 'rotate-90',
                !node.hasChildren && 'invisible',
              )}
              aria-hidden="true"
            />

            {node.type === 'TOWER' && (
              <Building2
                className="size-4 shrink-0 text-(--color-content-muted)"
                aria-hidden="true"
              />
            )}
            {node.type === 'FLOOR' && (
              // A cota do pavimento, como num corte: 0 é o térreo, −1 o subsolo.
              <span
                aria-hidden="true"
                className="flex h-5 min-w-6 shrink-0 items-center justify-center rounded border border-(--color-border-subtle) bg-(--color-surface-raised) px-1 font-mono text-[11px] font-semibold leading-none tabular text-(--color-content-muted)"
              >
                {node.marker}
              </span>
            )}
            {node.type === 'ACTIVITY' && (
              // O traço na cor da disciplina — a mesma cor que ela tem no cronograma.
              <span
                aria-hidden="true"
                className="h-4 w-[3px] shrink-0 rounded-full bg-(--color-state-neutral)"
                {...(node.color === undefined ? {} : { style: { backgroundColor: node.color } })}
              />
            )}
            {node.type === 'ITEM' && (
              <Package
                className="size-3.5 shrink-0 text-(--color-content-muted)"
                aria-hidden="true"
              />
            )}

            <span className="min-w-0 flex-1 truncate">{node.label}</span>

            {node.status !== undefined && node.status !== 'PLANNED' && (
              <Badge tone={STATUS_TONE[node.status]} className="shrink-0">
                {ACTIVITY_STATUS_LABEL[node.status]}
              </Badge>
            )}

            {node.summary !== '' && (
              <span
                className={cn(
                  'shrink-0 text-xs text-(--color-content-muted) tabular',
                  // No celular o resumo da torre e do pavimento cede o lugar ao nome.
                  (node.type === 'TOWER' || node.type === 'FLOOR') && 'hidden sm:inline',
                )}
              >
                {node.summary}
              </span>
            )}

            {(() => {
              const actions = actionsFor(node);
              const hasAny =
                actions.rename || actions.addItem || actions.editItem || actions.remove;
              if (!hasAny) return null;

              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      // Fora da ordem de tabulação: dentro de uma árvore ARIA só um nó é
                      // focável por vez. Pelo teclado, use F2 e Delete.
                      tabIndex={-1}
                      aria-label={`Ações de ${node.label}`}
                      className="shrink-0 rounded p-1 text-(--color-content-muted) opacity-0 hover:bg-(--color-surface-raised) focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <MoreVertical className="size-4" aria-hidden="true" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {actions.addItem && (
                      <DropdownMenuItem onSelect={() => onAddItem?.(node)}>
                        <Plus aria-hidden="true" />
                        Adicionar item
                      </DropdownMenuItem>
                    )}
                    {actions.editItem && (
                      <DropdownMenuItem onSelect={() => onEditItem?.(node)}>
                        <Pencil aria-hidden="true" />
                        Editar item
                      </DropdownMenuItem>
                    )}
                    {actions.rename && (
                      <DropdownMenuItem onSelect={() => onRename?.(node)}>
                        <Pencil aria-hidden="true" />
                        Renomear
                      </DropdownMenuItem>
                    )}
                    {actions.remove && (actions.rename || actions.addItem || actions.editItem) && (
                      <DropdownMenuSeparator />
                    )}
                    {actions.remove && (
                      <DropdownMenuItem destructive onSelect={() => onDelete?.(node)}>
                        <Trash2 aria-hidden="true" />
                        Remover
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}
